import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ArrowPathIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';

const ResumePreview = ({
  html,
  loading = false,
  hasWatermark = false,
  onFullscreen,
  className = ''
}) => {
  const iframeRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (iframeRef.current && html) {
      const iframe = iframeRef.current;
      const doc = iframe.contentDocument || iframe.contentWindow.document;

      // Write the HTML content
      doc.open();
      doc.write(html);
      doc.close();

      // Calculate scale to fit preview
      const updateScale = () => {
        const containerWidth = iframe.parentElement.clientWidth - 32; // padding
        const pageWidth = 816; // 8.5in at 96dpi
        const newScale = Math.min(containerWidth / pageWidth, 1);
        setScale(newScale);
      };

      updateScale();
      window.addEventListener('resize', updateScale);
      return () => window.removeEventListener('resize', updateScale);
    }
  }, [html]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center h-full bg-gray-100 ${className}`}>
        <div className="text-center">
          <ArrowPathIcon className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-gray-500">Generating preview...</p>
        </div>
      </div>
    );
  }

  if (!html) {
    return (
      <div className={`flex items-center justify-center h-full bg-gray-100 ${className}`}>
        <div className="text-center px-6">
          <div className="w-16 h-20 bg-gray-200 rounded-lg mx-auto mb-4 flex items-center justify-center">
            <div className="w-10 h-1 bg-gray-300 rounded mb-1" />
            <div className="w-8 h-1 bg-gray-300 rounded" />
          </div>
          <p className="text-sm text-gray-500">
            Your resume preview will appear here as you answer questions
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative h-full bg-gray-100 overflow-auto ${className}`}>
      {/* Toolbar */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Preview</span>
          {hasWatermark && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
              Watermarked
            </span>
          )}
        </div>
        {onFullscreen && (
          <button
            onClick={onFullscreen}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
            title="Fullscreen preview"
          >
            <ArrowsPointingOutIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Preview container */}
      <div className="p-4">
        <div
          className="bg-white shadow-lg mx-auto"
          style={{
            width: 816, // 8.5in at 96dpi
            height: 1056, // 11in at 96dpi
            transform: `scale(${scale})`,
            transformOrigin: 'top center'
          }}
        >
          <iframe
            ref={iframeRef}
            title="Resume Preview"
            className="w-full h-full border-0"
            sandbox="allow-same-origin"
          />
        </div>
      </div>
    </div>
  );
};

ResumePreview.propTypes = {
  html: PropTypes.string,
  loading: PropTypes.bool,
  hasWatermark: PropTypes.bool,
  onFullscreen: PropTypes.func,
  className: PropTypes.string
};

export default ResumePreview;
